import { useEffect, useRef, useState, useCallback } from "react";
import { RotateCw, MousePointerClick, Move, Maximize2, Orbit } from "lucide-react";

// Interactive canvas 3D surface. Fills its parent (resizable). Rotate mode = drag
// to orbit. Edit mode = click a point and drag up/down to change its cell value.
export function MapSurface3D({ grid, xLabels, yLabels, xTitle = "X", yTitle = "Y", onCellChange, onEditStart, selected, onSelect, selRegion = null, orig = null, liveCell = null, visited = null, afrColor = null }) {
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const vertsRef = useRef([]);
  const rangeRef = useRef({ vmin: 0, vmax: 1 });
  const [rot, setRot] = useState({ yaw: -0.7, pitch: -0.5 });
  const [mode, setMode] = useState("rotate");
  const [dim, setDim] = useState({ w: 640, h: 360 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [autoRot, setAutoRot] = useState(false);

  // Auto-rotate: orbit yaw (left/right) and gently oscillate pitch (up/down), paused while dragging.
  useEffect(() => {
    if (!autoRot) return;
    let raf, last = performance.now(), t = 0;
    const tick = (now) => {
      const dt = now - last; last = now; t += dt;
      if (!dragRef.current) setRot((r) => ({
        yaw: r.yaw + dt * 0.00022,
        pitch: -0.5 + Math.sin(t * 0.0004) * 0.1,
      }));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoRot]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDim({ w: Math.max(280, el.clientWidth), h: Math.max(220, el.clientHeight) }));
    ro.observe(el); setDim({ w: Math.max(280, el.clientWidth), h: Math.max(220, el.clientHeight) });
    return () => ro.disconnect();
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !grid || !grid.length) return;
    const W = dim.w, H = dim.h; canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d"); ctx.clearRect(0, 0, W, H);
    const rows = grid.length, cols = grid[0].length;
    const hasOrig = orig && orig.length === rows && orig[0] && orig[0].length === cols;
    const flat = grid.flat().concat(hasOrig ? orig.flat() : []); const vmin = Math.min(...flat), vmax = Math.max(...flat);
    rangeRef.current = { vmin, vmax };
    const cy = Math.cos(rot.yaw), sy = Math.sin(rot.yaw), cp = Math.cos(rot.pitch), sp = Math.sin(rot.pitch);
    const project = (i, j, v) => {
      const x = cols > 1 ? j / (cols - 1) - 0.5 : 0;
      const z = rows > 1 ? i / (rows - 1) - 0.5 : 0;
      const y = vmax === vmin ? 0 : (v - vmin) / (vmax - vmin) - 0.5;
      const x1 = x * cy - z * sy, z1 = x * sy + z * cy;
      const y1 = y * cp - z1 * sp, z2 = y * sp + z1 * cp;
      const persp = 2.6 / (2.6 + z2);
      return { sx: W / 2 + pan.x + x1 * persp * W * 0.46 * zoom, sy: H / 2 + pan.y - y1 * persp * H * 0.6 * zoom - H * 0.02, depth: z2 };
    };
    const color = (v) => { const t = vmax === vmin ? 0.5 : (v - vmin) / (vmax - vmin); return `hsl(${Math.round((1 - t) * 185)},78%,${40 + t * 12}%)`; };

    const verts = [];
    for (let i = 0; i < rows; i++) for (let j = 0; j < cols; j++) { const p = project(i, j, grid[i][j]); verts.push({ r: i, c: j, sx: p.sx, sy: p.sy }); }
    vertsRef.current = verts;

    // --- reference scaffold (dashed floor grid + value-tick walls + value labels), TunerPro-style ---
    {
      const ticks = 7;
      const stepCg = Math.max(1, Math.ceil((cols - 1) / 12));
      const stepRg = Math.max(1, Math.ceil((rows - 1) / 12));
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(150,150,160,0.45)";
      // floor grid at the base (v = vmin)
      for (let i = 0; i < rows; i += stepRg) { ctx.beginPath(); for (let j = 0; j < cols; j++) { const p = project(i, j, vmin); j === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke(); }
      for (let j = 0; j < cols; j += stepCg) { ctx.beginPath(); for (let i = 0; i < rows; i++) { const p = project(i, j, vmin); i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke(); }
      // value-tick horizontal lines on the two back walls (j = cols-1 and i = 0)
      for (let t = 0; t <= ticks; t++) {
        const v = vmin + (vmax - vmin) * t / ticks;
        ctx.beginPath(); for (let i = 0; i < rows; i++) { const p = project(i, cols - 1, v); i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke();
        ctx.beginPath(); for (let j = 0; j < cols; j++) { const p = project(0, j, v); j === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke();
      }
      ctx.restore();
      // value labels along the right vertical edge (corner i=0, j=cols-1)
      ctx.fillStyle = "#93b0ff"; ctx.font = "10px 'JetBrains Mono', monospace"; ctx.textAlign = "left";
      for (let t = 0; t <= ticks; t++) {
        const v = vmin + (vmax - vmin) * t / ticks;
        const p = project(0, cols - 1, v);
        ctx.fillText((Math.round(v * 100) / 100).toFixed(2), p.sx + 6, p.sy + 3);
      }
    }

    const quads = [];
    for (let i = 0; i < rows - 1; i++) for (let j = 0; j < cols - 1; j++) {
      const p = [project(i, j, grid[i][j]), project(i, j + 1, grid[i][j + 1]), project(i + 1, j + 1, grid[i + 1][j + 1]), project(i + 1, j, grid[i + 1][j])];
      const avg = (grid[i][j] + grid[i][j + 1] + grid[i + 1][j + 1] + grid[i + 1][j]) / 4;
      const depth = (p[0].depth + p[1].depth + p[2].depth + p[3].depth) / 4;
      quads.push({ p, avg, depth, r: i, c: j });
    }
    quads.sort((a, b) => b.depth - a.depth);
    quads.forEach((q) => {
      ctx.beginPath(); ctx.moveTo(q.p[0].sx, q.p[0].sy);
      for (let k = 1; k < 4; k++) ctx.lineTo(q.p[k].sx, q.p[k].sy);
      ctx.closePath(); ctx.fillStyle = color(q.avg); ctx.fill();
      if (selRegion && q.r >= selRegion.rs && q.r + 1 <= selRegion.re && q.c >= selRegion.cs && q.c + 1 <= selRegion.ce) {
        ctx.fillStyle = "rgba(239,68,68,0.42)"; ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.92)"; ctx.lineWidth = 1.2; ctx.stroke();
      } else {
        ctx.strokeStyle = "rgba(2,6,12,0.4)"; ctx.lineWidth = 0.5; ctx.stroke();
      }
    });

    // --- live trace ("jejak"): visited cells as AFR-coloured dots sitting on the surface ---
    if (visited) {
      Object.keys(visited).forEach((key) => {
        const s = key.split(",");
        const ri = +s[0], cj = +s[1];
        if (!grid[ri] || grid[ri][cj] == null) return;
        const v = visited[key];
        const afr = v && typeof v === "object" ? v.afr : null;
        const p = project(ri, cj, grid[ri][cj]);
        ctx.beginPath(); ctx.arc(p.sx, p.sy, 3.2, 0, 7);
        ctx.fillStyle = afrColor ? afrColor(afr) : "rgba(217,70,239,0.55)";
        ctx.fill();
        ctx.lineWidth = 0.6; ctx.strokeStyle = "rgba(0,0,0,0.45)"; ctx.stroke();
      });
    }
    // --- live operating point (current cell), pulsing halo ---
    if (liveCell && grid[liveCell.r] && grid[liveCell.r][liveCell.c] != null) {
      const p = project(liveCell.r, liveCell.c, grid[liveCell.r][liveCell.c]);
      const t = (performance.now() % 1000) / 1000;
      const rad = 6 + Math.sin(t * Math.PI * 2) * 1.8;
      ctx.beginPath(); ctx.arc(p.sx, p.sy, rad + 6, 0, 7);
      ctx.fillStyle = `rgba(250,204,21,${0.10 + 0.14 * (1 - t)})`; ctx.fill();
      ctx.beginPath(); ctx.arc(p.sx, p.sy, rad, 0, 7);
      ctx.fillStyle = "#fde047"; ctx.strokeStyle = "#1a1205"; ctx.lineWidth = 1.6; ctx.fill(); ctx.stroke();
    }

    if (selRegion) verts.forEach((vt) => {
      if (vt.r >= selRegion.rs && vt.r <= selRegion.re && vt.c >= selRegion.cs && vt.c <= selRegion.ce) {
        ctx.beginPath(); ctx.arc(vt.sx, vt.sy, 2.5, 0, 7); ctx.fillStyle = "#ef4444"; ctx.fill();
      }
    });

    if (mode === "edit") verts.forEach((vt) => { ctx.beginPath(); ctx.arc(vt.sx, vt.sy, 2, 0, 7); ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.fill(); });

    if (hasOrig) {
      ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = 1.4; ctx.shadowColor = "rgba(0,0,0,0.7)"; ctx.shadowBlur = 2;
      for (let i = 0; i < rows; i++) { ctx.beginPath(); for (let j = 0; j < cols; j++) { const p = project(i, j, orig[i][j]); j === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke(); }
      for (let j = 0; j < cols; j++) { ctx.beginPath(); for (let i = 0; i < rows; i++) { const p = project(i, j, orig[i][j]); i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy); } ctx.stroke(); }
      ctx.shadowBlur = 0;
    }

    if (selected && grid[selected.r] && grid[selected.r][selected.c] != null) {
      const p = project(selected.r, selected.c, grid[selected.r][selected.c]);
      ctx.beginPath(); ctx.arc(p.sx, p.sy, 6, 0, 7); ctx.fillStyle = "#ef4444"; ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.font = "bold 11px monospace"; ctx.textAlign = "center";
      ctx.fillText(String(Math.round(grid[selected.r][selected.c] * 100) / 100), p.sx, p.sy - 12);
    }

    ctx.fillStyle = "#7dd3fc"; ctx.font = "10px 'JetBrains Mono', monospace";
    const lbl = (arr, idx) => { if (!arr || arr[idx] == null) return idx; const n = Number(arr[idx]); return Number.isFinite(n) ? (Number.isInteger(n) ? n : Math.round(n * 10) / 10) : arr[idx]; };
    const stepC = Math.max(1, Math.ceil(cols / 12)), stepR = Math.max(1, Math.ceil(rows / 12));
    // X (cols) labels along the front edge (row 0)
    ctx.textAlign = "right";
    for (let j = 0; j < cols; j += stepC) { const p = project(0, j, vmin); ctx.fillText(String(lbl(xLabels, j)), p.sx - 4, p.sy + 11); }
    // Y (rows) labels along the right edge (last col)
    ctx.textAlign = "left";
    for (let i = 0; i < rows; i += stepR) { const p = project(i, cols - 1, vmin); ctx.fillText(String(lbl(yLabels, i)), p.sx + 8, p.sy + 8); }
    ctx.fillStyle = "#ef4444"; ctx.font = "bold 11px 'JetBrains Mono', monospace"; ctx.textAlign = "center";
    const pcx = project(0, Math.floor(cols / 2), vmin); ctx.fillText(xTitle, pcx.sx - 18, pcx.sy + 26);
    const pry = project(Math.floor(rows / 2), cols - 1, vmin); ctx.fillText(yTitle, pry.sx + 34, pry.sy + 22);
  }, [grid, xLabels, yLabels, rot, dim, xTitle, yTitle, mode, selected, selRegion, orig, zoom, pan, liveCell, visited, afrColor]);

  useEffect(() => { draw(); }, [draw]);

  // Keep the live marker pulsing while a live cell is present.
  useEffect(() => {
    if (!liveCell) return;
    let raf;
    const tick = () => { draw(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [liveCell, draw]);

  // Smooth wheel zoom (native non-passive listener so we can preventDefault the page scroll).
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const onWheel = (ev) => { ev.preventDefault(); const f = Math.exp(-ev.deltaY * 0.0015); setZoom((z) => Math.min(4, Math.max(0.4, z * f))); };
    c.addEventListener("wheel", onWheel, { passive: false });
    return () => c.removeEventListener("wheel", onWheel);
  }, []);

  const rel = (e) => { const rect = canvasRef.current.getBoundingClientRect(); return { x: (e.clientX - rect.left) * (dim.w / rect.width), y: (e.clientY - rect.top) * (dim.h / rect.height) }; };
  const nearest = (x, y) => { let best = null, bd = 1e9; vertsRef.current.forEach((v) => { const d = (v.sx - x) ** 2 + (v.sy - y) ** 2; if (d < bd) { bd = d; best = v; } }); return best && bd <= 26 * 26 ? best : null; };

  const onDown = (e) => {
    if (e.touches && e.touches.length === 2) {
      if (e.cancelable) e.preventDefault();
      const a = e.touches[0], b = e.touches[1];
      dragRef.current = { pinch: true, dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        midx: (a.clientX + b.clientX) / 2, midy: (a.clientY + b.clientY) / 2, zoom0: zoom, panx0: pan.x, pany0: pan.y };
      return;
    }
    if (e.touches && e.cancelable) e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    if (mode === "edit") {
      const { x, y } = rel(pt); const v = nearest(x, y);
      if (v) { onEditStart && onEditStart(); onSelect && onSelect({ r: v.r, c: v.c }); dragRef.current = { edit: true, r: v.r, c: v.c, startY: pt.clientY, startVal: grid[v.r][v.c] }; return; }
    }
    if (mode === "pan") { dragRef.current = { panning: true, x: pt.clientX, y: pt.clientY, px: pan.x, py: pan.y }; return; }
    dragRef.current = { x: pt.clientX, y: pt.clientY, ...rot };
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    if (e.touches && e.touches.length === 2 && dragRef.current.pinch) {
      if (e.cancelable) e.preventDefault();
      const a = e.touches[0], b = e.touches[1];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const midx = (a.clientX + b.clientX) / 2, midy = (a.clientY + b.clientY) / 2;
      const pr = dragRef.current;
      const f = pr.dist ? dist / pr.dist : 1;
      setZoom(Math.min(4, Math.max(0.4, pr.zoom0 * f)));
      setPan({ x: pr.panx0 + (midx - pr.midx), y: pr.pany0 + (midy - pr.midy) });
      return;
    }
    if (e.touches && e.cancelable) e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    if (dragRef.current.edit) {
      if (!onCellChange) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const { vmin, vmax } = rangeRef.current;
      const scale = (vmax - vmin) / (rect.height * 0.5) || 0.1;
      let nv = dragRef.current.startVal + (dragRef.current.startY - pt.clientY) * scale;
      nv = Math.round(nv * 100) / 100;
      onCellChange(dragRef.current.r, dragRef.current.c, nv);
    } else if (dragRef.current.panning) {
      setPan({ x: dragRef.current.px + (pt.clientX - dragRef.current.x), y: dragRef.current.py + (pt.clientY - dragRef.current.y) });
    } else {
      const dx = pt.clientX - dragRef.current.x, dy = pt.clientY - dragRef.current.y;
      setRot({ yaw: dragRef.current.yaw + dx * 0.01, pitch: Math.max(-1.45, Math.min(-0.05, dragRef.current.pitch + dy * 0.01)) });
    }
  };
  const onUp = () => { dragRef.current = null; };

  const btn = (m, Icon, label, tid) => (
    <button data-testid={tid} onClick={() => setMode(m)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${mode === m ? "bg-red-500 text-slate-950" : "bg-[#121824] text-slate-300"}`}><Icon className="h-3.5 w-3.5" /> {label}</button>
  );

  return (
    <div className="w-full h-full flex flex-col" data-testid="mapedit-3d">
      <div className="flex items-center gap-1.5 mb-2 shrink-0">
        {btn("rotate", RotateCw, "Rotate", "mapedit-3d-rotate")}
        {btn("pan", Move, "Pan", "mapedit-3d-pan")}
        {btn("edit", MousePointerClick, "Edit points", "mapedit-3d-edit")}
        <button data-testid="mapedit-3d-autorotate" onClick={() => setAutoRot((v) => !v)} title="Auto-rotate the surface" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${autoRot ? "bg-emerald-500 text-slate-950" : "bg-[#121824] text-slate-300"}`}><Orbit className={`h-3.5 w-3.5 ${autoRot ? "animate-spin" : ""}`} style={autoRot ? { animationDuration: "3s" } : undefined} /> Auto</button>
        <button data-testid="mapedit-3d-reset" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); setRot({ yaw: -0.7, pitch: -0.5 }); setAutoRot(false); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-[#121824] text-slate-300 hover:bg-slate-700"><Maximize2 className="h-3.5 w-3.5" /> Reset</button>
        <span className="text-[10px] text-slate-500 ml-2 font-mono-tt">{mode === "edit" ? "click a point · drag up/down" : mode === "pan" ? "drag to geser · scroll/pinch = zoom" : "drag = rotate · scroll/pinch = zoom"}</span>
      </div>
      <div ref={boxRef} className="flex-1 min-h-0">
        <canvas ref={canvasRef} style={{ touchAction: "none" }}
          className={`w-full h-full rounded-lg ${mode === "edit" ? "cursor-crosshair" : "cursor-grab active:cursor-grabbing"}`}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} />
      </div>
    </div>
  );
}
